import { motion } from "framer-motion";
import { ArrowUpRight, Anchor, Bike, Croissant, IceCreamCone, UtensilsCrossed, Waves } from "lucide-react";
import { EASE } from "@/lib/scroll";

const NEARBY = [
  { icon: Waves, label: "Connaught Beach", time: "300 steps away" },
  {
    icon: Croissant,
    label: "Village Green Bakery",
    time: "short bike ride",
    href: "https://www.villagegreenbakery.com/",
  },
  {
    icon: IceCreamCone,
    label: "Moonlight Inn · ice cream & licensed dinners",
    time: "short bike ride",
    href: "https://www.themoonlightinn.com/",
  },
  { icon: UtensilsCrossed, label: "Birchwood Inn · Thursday Rib Night", time: "short ride" },
  { icon: Anchor, label: "The Historic Pier", time: "end of the road" },
];

export default function Location() {
  return (
    <section
      id="location"
      data-testid="location-section"
      className="bg-[#EFECE6] py-24 sm:py-36"
    >
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 sm:px-10 lg:grid-cols-2 lg:gap-20">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="relative pb-20"
        >
          <div className="rotate-[-1.5deg] border border-[#1E3323]/15 bg-[#F7F4EF] p-3 shadow-xl shadow-[#1E3323]/10 transition-transform duration-500 hover:rotate-0">
            <img
              src="/photos/garden-path.webp"
              alt="Stone-walled garden path leading to the cottage porch"
              data-testid="location-image"
              loading="lazy"
              className="max-h-[520px] w-full object-cover"
            />
          </div>
          <div
            data-testid="location-arch-frame"
            className="absolute -bottom-4 right-0 w-3/5 rotate-[2deg] border border-[#1E3323]/15 bg-[#F7F4EF] p-2 shadow-xl shadow-[#1E3323]/15 transition-transform duration-500 hover:rotate-0 sm:-right-4"
          >
            <img
              src="/photos/welcome-arch.webp"
              alt="The Victoria Beach welcome arch over the entrance road"
              loading="lazy"
              className="w-full object-cover"
            />
          </div>
          <p className="mt-5 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-[#767970]">
            The path to the front porch · the arch that welcomes you to VB
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
        >
          <div className="mb-6 flex items-center gap-4">
            <span className="h-px w-12 bg-[#C8634B]" />
            <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#C8634B]">
              Find Us
            </p>
          </div>
          <h2 className="font-serif text-3xl leading-snug tracking-tight text-[#1E3323] sm:text-4xl lg:text-5xl">
            Tucked into the trees on{" "}
            <span className="italic text-[#C8634B]">Seventh Avenue.</span>
          </h2>
          <p className="mt-6 max-w-md text-sm leading-relaxed text-[#4A4D46] sm:text-base">
            The cottage sits inside the Victoria Beach Restricted Area on
            the south shore of Lake Winnipeg,
            about an hour north of Winnipeg. From July to Labour Day no cars
            come in: park at the gate, load the wagon, and pedal in. Taxis run
            to and from the gate when you need them, and the community store
            stocks everything from milk and eggs to slushies.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <span
              data-testid="location-address-chip"
              className="rounded-full border border-[#1E3323]/20 bg-white/60 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-[#1E3323]"
            >
              7th Avenue · Victoria Beach, MB
            </span>
            <span className="rounded-full border border-[#1E3323]/20 bg-white/60 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-[#1E3323]">
              50.6978° N · 96.5617° W
            </span>
          </div>

          <ul className="mt-10 divide-y divide-[#1E3323]/10 border-y border-[#1E3323]/10">
            {NEARBY.map((item, i) => {
              const inner = (
                <>
                  <span className="flex items-center gap-4">
                    <span className="rounded-full border border-[#1E3323]/15 p-2.5 text-[#1E3323]">
                      <item.icon size={16} strokeWidth={1.75} />
                    </span>
                    <span className="text-sm font-medium text-[#1A1C19]">
                      {item.label}
                    </span>
                    {item.href && (
                      <ArrowUpRight size={13} className="text-[#C8634B]" />
                    )}
                  </span>
                  <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-[#767970]">
                    {item.time}
                  </span>
                </>
              );
              return (
                <li key={item.label} data-testid={`location-nearby-${i + 1}`}>
                  {item.href ? (
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`location-nearby-link-${i + 1}`}
                      className="flex items-center justify-between py-4 transition-colors duration-300 hover:text-[#C8634B]"
                    >
                      {inner}
                    </a>
                  ) : (
                    <div className="flex items-center justify-between py-4">
                      {inner}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>

          <div
            data-testid="bakery-vignette"
            className="mt-10 border-l-2 border-[#C8634B] pl-6"
          >
            <p className="font-serif text-xl italic leading-snug text-[#1E3323] sm:text-2xl">
              A Village Green Bakery tradition
            </p>
            <p className="mt-3 max-w-md text-sm leading-relaxed text-[#4A4D46]">
              Nestled into the heart of the Village Green, the completely
              renovated bakery turns out staples and treats every single day.
              Parents and grandparents now bring their children and
              grandchildren down for the same freshly baked bread they once
              lined up for as little kids.
            </p>
          </div>

          <div
            data-testid="arrival-notes"
            className="mt-10 rounded-xl border border-[#1E3323]/12 bg-white/60 p-6"
          >
            <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.22em] text-[#C8634B]">
              Arrival notes: good to know
            </p>
            <ul className="space-y-4">
              <li data-testid="arrival-note-1">
                <p className="text-sm font-semibold text-[#1A1C19]">
                  Parking pass, sorted
                </p>
                <p className="mt-0.5 text-sm leading-relaxed text-[#4A4D46]">
                  Every guest vehicle needs a parking pass for the
                  front-entrance lot. It's gate-controlled, so your car stays
                  safe all week.
                </p>
              </li>
              <li data-testid="arrival-note-2">
                <p className="text-sm font-semibold text-[#1A1C19]">
                  Taxis at the gate
                </p>
                <p className="mt-0.5 text-sm leading-relaxed text-[#4A4D46]">
                  Bringing half the house? Taxis wait at the parking lot to
                  ferry luggage and supplies gate-to-cottage, and you can call
                  one back from 7th Avenue anytime.
                </p>
              </li>
              <li data-testid="arrival-note-3">
                <p className="text-sm font-semibold text-[#1A1C19]">
                  Bikes: some to borrow, sizes vary
                </p>
                <p className="mt-0.5 text-sm leading-relaxed text-[#4A4D46]">
                  There will be bikes waiting, but we can't promise the right
                  size or count for your crew, so if you're particular, bring
                  your own.
                </p>
              </li>
            </ul>
          </div>

          <p className="mt-6 font-mono text-[10px] uppercase leading-relaxed tracking-[0.18em] text-[#767970]">
            For everyone's privacy, we share the exact address with confirmed
            guests
          </p>
        </motion.div>
      </div>
    </section>
  );
}
